/** Every way the console writes a time, in one file.
 *
 *  This merges the formatters the old pages each kept (`when` in Dashboard and
 *  RedeemPosts, `formatWhen` in Redeem, `shortWhen`/`formatTime` in utils).
 *  Rules: 24-hour clock, month and day as "10. 17.", the year only when it is
 *  not this year, and slot input always local → UTC ISO (`fromLocalInput`) —
 *  the 10-16 early post came from comparing a local time as if it were UTC. */

const pad = (n: number) => String(n).padStart(2, '0')
const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토']
const DAY_MS = 86_400_000

/** A Date from an ISO string, or null for empty and unparseable input. */
export function parseTime(iso?: string | null): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

function midnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/** Whole calendar days from `now` to `d` in the browser's zone: 0 today,
 *  1 tomorrow, -1 yesterday. */
export function dayOffset(d: Date, now = new Date()): number {
  return Math.round((midnight(d) - midnight(now)) / DAY_MS)
}

function hm(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function md(d: Date, now: Date): string {
  const base = `${d.getMonth() + 1}. ${d.getDate()}.`
  return d.getFullYear() === now.getFullYear() ? base : `${d.getFullYear()}. ${base}`
}

/** "10. 17. 21:00" — the year only when it is another year. */
export function formatAbsolute(iso?: string | null, now = new Date()): string {
  const d = parseTime(iso)
  if (!d) return '—'
  return `${md(d, now)} ${hm(d)}`
}

/** "2026. 10. 17. (금) 21:00:05" — for tooltips. */
export function formatFull(iso?: string | null): string {
  const d = parseTime(iso)
  if (!d) return ''
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}. (${WEEKDAY[d.getDay()]}) ${hm(d)}:${pad(d.getSeconds())}`
}

/** "오늘 13:35", "어제 13:35", "내일 09:00", else "10. 17. 13:35". The desk
 *  turns over daily, so this is the form tables use. */
export function formatShort(iso?: string | null, now = new Date()): string {
  const d = parseTime(iso)
  if (!d) return '—'
  const off = dayOffset(d, now)
  if (off === 0) return `오늘 ${hm(d)}`
  if (off === -1) return `어제 ${hm(d)}`
  if (off === 1) return `내일 ${hm(d)}`
  return `${md(d, now)} ${hm(d)}`
}

/** "방금", "12분 뒤", "3시간 전", "2일 뒤". */
export function formatRelative(iso?: string | null, now = new Date()): string {
  const d = parseTime(iso)
  if (!d) return '—'
  const mins = Math.round((d.getTime() - now.getTime()) / 60_000)
  const abs = Math.abs(mins)
  const side = mins >= 0 ? '뒤' : '전'
  if (abs < 1) return '방금'
  if (abs < 60) return `${abs}분 ${side}`
  if (abs < 60 * 24) return `${Math.round(abs / 60)}시간 ${side}`
  return `${Math.round(abs / (60 * 24))}일 ${side}`
}

/** "10. 17. 21:00 (2시간 뒤)" — a slot with how far off it is. */
export function formatSlot(iso?: string | null, now = new Date()): string {
  const d = parseTime(iso)
  if (!d) return ''
  return `${formatAbsolute(iso, now)} (${formatRelative(iso, now)})`
}

/** A unix-seconds timestamp in full, for job runs. */
export function formatUnix(unix?: number | null): string {
  if (!unix) return '-'
  return formatFull(new Date(unix * 1000).toISOString())
}

/** A `datetime-local` value ("2026-10-17T21:00") in the browser's zone. */
export function toLocalInput(iso?: string | null): string {
  const d = parseTime(iso)
  if (!d) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${hm(d)}`
}

/** A `datetime-local` value read in the browser's zone, as a UTC ISO string —
 *  the only form a slot is sent to the server in. Empty or malformed → null. */
export function fromLocalInput(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local.trim())
  if (!m) return null
  const [, y, mo, d, h, mi, s] = m
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0))
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/** The text the server answers with too, so the two never disagree. */
export const PAST_SLOT_MESSAGE = '지난 시각에는 예약할 수 없다'

/** A slot earlier than a minute from now is refused by the server; this is the
 *  same test, so the form says so before it is sent. Null when it is fine. */
export function pastSlotError(iso: string | null | undefined, now = new Date()): string | null {
  const d = parseTime(iso)
  if (!d) return null
  return d.getTime() < now.getTime() + 60_000 ? PAST_SLOT_MESSAGE : null
}

/** "2026-10-17" in the browser's zone — the key a calendar day is filed under. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** A day heading: "오늘 10. 17. (금)", "내일 10. 18. (토)", "10. 19. (일)". */
export function dayLabel(d: Date, now = new Date()): string {
  const base = `${md(d, now)} (${WEEKDAY[d.getDay()]})`
  const off = dayOffset(d, now)
  if (off === 0) return `오늘 ${base}`
  if (off === 1) return `내일 ${base}`
  if (off === -1) return `어제 ${base}`
  return base
}

/** Days until a "YYYY-MM-DD" date (an expiry), counted in the browser's zone:
 *  0 on the day itself, negative after it, null when there is none. */
export function daysUntil(date?: string | null, now = new Date()): number | null {
  if (!date) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return dayOffset(d, now)
}

/** "D-9", "D-day", "D+2". */
export function formatDday(days: number | null): string {
  if (days === null) return '—'
  if (days === 0) return 'D-day'
  return days > 0 ? `D-${days}` : `D+${-days}`
}

/** The browser's zone name, for "Asia/Seoul 기준" under a slot input. */
export function localZoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '로컬 시간'
  } catch {
    return '로컬 시간'
  }
}
